import React, { useState } from 'react';
import { map, orderBy, get, isEmpty, split, forEach, includes } from 'lodash';
import { withApollo } from 'react-apollo';
import * as yup from 'yup';
import { Button, FlexContainer, FormInput, FormSelect, Main, SalesChannelSelect, SpaceBetween } from '../../common';
import { MediaListener } from '../../utils';
import { autoTypeUser, createSalesChannelsPayload, statusUser, thirdPartyCompanies, validateRut } from '../../../utils/misc';
import { GET_USER_BY_EMAIL_QUERY } from '../../graphql/GetPickerByEmail';
import { format } from 'rut.js';
import { UPDATE_PICKERS } from '../../graphql/updatePickers';

const ShoppersForm = ({ client }) => {
    const [firstName, onChangeFirstName] = useState(null);
    const [lastName, onChangeLastName] = useState(null);
    const [document, onChangeDocument] = useState(null);
    const [email, onChangeEmail] = useState(null);
    const [phone, onChangePhone] = useState(null);
    const [company, onChangeCompany] = useState(null);
    const [salesChannels, onChangeSalesChannels] = useState(null);
    const [fieldErrors, setFieldErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const emailSpecialCharValidation = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;


    const documentSchema = yup.string()
        .test('documentId', 'document-El RUT ingresado no es válido',
            function (value) {
                return validateRut(value);
        })
        .required('document-Campo obligatorio')
        .nullable();
    
    const phoneSchema = yup.string()
        .required('phone-Campo obligatorio')
        .min(9, 'phone-Se requiere mínimo de 9 dígitos')
        .max(9, 'phone-Máximo permitido de 9 dígitos')
        .nullable();

    const emailSchema = yup.string()
        .email('email-Ingresa una dirección de email válida')
        .required('email-Campo obligatorio')
        .matches(emailSpecialCharValidation, 'El email contiene caracteres no permitidos')
        .nullable();

    const validationSchema = yup.object().shape({
        firstName: yup.string()
            .required('firstName-Campo obligatorio')
            .nullable(),
        lastName: yup.string()
            .required('lastName-Campo obligatorio')
            .nullable(),
        document: documentSchema,
        email: emailSchema
            .test('emailAlreadyExists', 'email-Este email ya está registrado', async (value) => {
                if (!value) return true;
                const data = await client.query({ query: GET_USER_BY_EMAIL_QUERY, variables: { email: value } });
                if (!get(data, 'data.pickerByEmail.id')) return true;
                return false;
            }),
        phone: phoneSchema,
        company: yup.string()
            .nullable()
            .required('company-Campo obligatorio'),
        salesChannels: yup.string()
            .nullable()
            .required('salesChannels-Campo obligatorio')
    });

    const removeError = field => {
        let errors = { ...fieldErrors };
        delete errors[field];
        setFieldErrors(errors);
    };

    const setFieldValue = async (value, callback, validate) => {
        const result = get(value, 'target.value', !isEmpty(get(value, 'value', null)) ? get(value, 'value', null) : null);
        switch (validate) {
            case 'document':
                documentSchema.validate(result)
                    .then(_ => removeError('document'))
                    .catch(err => errorsResolver(err, false));
                break;
            case 'email':
                    emailSchema.validate(result)
                        .then(_ => removeError('email'))
                        .catch(err => errorsResolver(err, false));
                    break;
            case 'phone':
                phoneSchema.validate(result)
                    .then(_ => removeError('phone'))
                    .catch(err => errorsResolver(err, false));
                break;
            default:
                break;
        }
        callback(includes(['document'], validate) ? format(result) : result);
    }

    const onSubmitResolver = async (data) => {
        setFieldErrors(null)
        const id = email;
        const payload = {
            firstName,
            lastName,
            document,
            phone,
            company: get(company, 'value'),
            status: '4',
            statusUser: get(statusUser[0], 'value'),
            groups: [sessionStorage.getItem('SHOPPERS_PROFILE_ID')],
            switchAutoSala: false,
            typeUser: get(autoTypeUser(get(company, 'value')), 'value'),
            salesChannelInfo: createSalesChannelsPayload(salesChannels, salesChannels)
        };
        await client.mutate({ mutation: UPDATE_PICKERS, variables: { id, payload } })
            .then(() => window.location.reload())
            .catch(error => console.log(error));
    }

    const errorsResolver = ({ errors }, reset = true) => {
        if (reset) setFieldErrors(null);
        let errorsMemory = reset ? {} : { ...fieldErrors };
        forEach(errors, error => {
            const splited = split(error, '-');
            errorsMemory = { ...errorsMemory, [splited[0]]: splited[1] };
        });
        setFieldErrors(errorsMemory);
    }

    const validateFields = async () => {
        setLoading(true);
        await validationSchema.validate({ firstName, lastName, document, email, phone, company, salesChannels }, { abortEarly: false })
            .then(onSubmitResolver)
            .catch(errorsResolver);
        setLoading(false);
    }

    const formFields = [
        {
            label: 'Nombres',
            onChange: value => setFieldValue(value, onChangeFirstName),
            error: get(fieldErrors, 'firstName', null)
        },
        {
            label: 'Apellidos',
            onChange: value => setFieldValue(value, onChangeLastName),
            error: get(fieldErrors, 'lastName', null)
        },
        {
            label: 'RUT',
            isDocumentId: true,
            maxLength: 12,
            onChange: value => setFieldValue(get(value, 'target.value') === '-' ? null : value, onChangeDocument, 'document'),
            error: get(fieldErrors, 'document', null),
            value: document
        },
        {
            label: 'Mail',
            onChange: (value) => setFieldValue(value, onChangeEmail, 'email'),
            error: get(fieldErrors, 'email', null)
        },
        {
            label: 'Teléfono',
            codePrefix: '+56',
            maxLength: 9,
            onChange: value => setFieldValue(value, onChangePhone, 'phone'),
            error: get(fieldErrors, 'phone', null),
            value: phone
        },
        {
            label: 'Empresa',
            Component: FormSelect,
            options: orderBy(thirdPartyCompanies(), 'label', 'asc'),
            onChange: value => setFieldValue(value, onChangeCompany),
            error: get(fieldErrors, 'company', null),
            value: company
        },
        {
            label: 'Sala',
            multiple: true,
            native: true,
            allSalesChannels: true,
            onChange: value => setFieldValue(value, onChangeSalesChannels),
            error: get(fieldErrors, 'salesChannels', null),
            Component: SalesChannelSelect
        }
    ];

    return (
        <Main background='f4f4f4' withFooter>
            <FlexContainer
                backgroundColor='#ffffff'
                margin={10}
                padding={10}
            >
                <MediaListener values={[false, true, true]}>
                    {({ currentValue: mobile }) => {
                        const width = mobile ? '100%' : 'calc(50% - 15px)';
                        return (
                            <FlexContainer
                                style={{ border: '1px solid #eaeaea' }}
                                flex={1}
                                borderRadius={5}
                                padding={10}
                                wrap
                            >
                                { map(formFields, ({ Component = FormInput, ...properties }, index) => (
                                    <>
                                        <FlexContainer style={{ width }}>
                                            <Component
                                                { ...properties }
                                            />
                                        </FlexContainer>
                                        <SpaceBetween show={!mobile && !(index%2)} />
                                    </>
                                )) }
                                <FlexContainer pT={20} style={{ width }} align='center'>
                                    <Button
                                        text='Guardar'
                                        loading={loading}
                                        disabled={loading || !firstName || !lastName || !document || !email || !phone || !company || !salesChannels}
                                        onClick={() => validateFields()}
                                    />
                                </FlexContainer>
                            </FlexContainer>
                        );
                    }}
                </MediaListener>
            </FlexContainer>
        </Main>
    );
}

export default withApollo(ShoppersForm);
